import React, { useState } from "react";
import PropTypes from "prop-types";

import Heading from "core/Heading";
import BodyText from "core/BodyText";
import RadioInput from "core/RadioInput";
import SrOnly from "core/SrOnly";
import CheckboxButton from "core/CheckboxButton";
import Tooltip from "core/Tooltip";

import PhotoshootNameChangeModal from "./PhotoshootNameChangeModal";

import EditIcon from "core/ReusableIcons/Edit";
import TrashIcon from "core/ReusableIcons/Trash";

import {
    Italic,
    HeaderFlexbox,
    MainHeadingContainer,
    TypeFlexbox,
    NameFlexbox,
    UploadedFilesControlsFlexbox,
    RadioFieldset,
    RadioLabel,
    ShootTitleBodyText,
    ShootNameBodyText,
    EditShootNameButton,
    SelectedPhotosControlsFlexbox,
    SelectedPhotosButton,
    SmallBlueTextButton
} from "./styles";

const HeadingSection = ({
    hasAcceptedFiles,
    hasAddedPhotos,
    isPhotoshootType,
    hasShootName,
    allowedUploadNum,
    radioOptions,
    handleRadioOnClick,
    selectedRadio,
    updateShootName,
    handleSelectAll,
    isSelectAll
}) => {
    const [isNameModalOpen, updateIsNameModalOpen] = useState(false);

    const handleOpenNameModal = () => updateIsNameModalOpen(true);
    const handleCloseNameModal = () => updateIsNameModalOpen(false);

    const handleEditSelected = e => {
        e.preventDefault();
        // todo in future PR: open carousel modal for all photos selected
    };

    const handleDeleteSelected = e => {
        e.preventDefault();
        // todo in future PR: delete all photos selected
    };

    return (
        <>
            <HeaderFlexbox justifyContent="space-between" alignItems="center">
                <MainHeadingContainer>
                    {hasAddedPhotos && isPhotoshootType ? (
                        <Heading type="h1" size="md">
                            Upload to <Italic>{hasShootName}</Italic>
                        </Heading>
                    ) : !hasAddedPhotos ? (
                        <Heading type="h1" size="md">
                            Upload Photos
                        </Heading>
                    ) : null}
                </MainHeadingContainer>

                <BodyText type="div" size="sm">
                    Submit up to {allowedUploadNum} photos
                </BodyText>
            </HeaderFlexbox>

            <TypeFlexbox alignItems="center">
                <RadioFieldset>
                    <SrOnly as="legend">Type of photos:</SrOnly>

                    {radioOptions.map((option, index) => {
                        return (
                            <RadioLabel key={index}>
                                <RadioInput
                                    onChange={e =>
                                        handleRadioOnClick(
                                            e.currentTarget.value
                                        )
                                    }
                                    name="type-of-photos"
                                    value={option.value}
                                    checked={selectedRadio === option.value}
                                />
                                {option.label}
                            </RadioLabel>
                        );
                    })}
                </RadioFieldset>

                <div>
                    <Tooltip
                        message='Images in a shoot have a common subject, aesthetic, location, etc. "Individual photos" may include a range of subjects and styles.'
                        clickToOpen
                    >
                        <SmallBlueTextButton>
                            What is the difference?
                        </SmallBlueTextButton>
                    </Tooltip>
                </div>
            </TypeFlexbox>

            <NameFlexbox>
                {isPhotoshootType && (
                    <>
                        <ShootTitleBodyText type="div" size="md" bold>
                            Photo Shoot Name:
                        </ShootTitleBodyText>
                        <ShootNameBodyText type="div" size="md">
                            {hasShootName}
                        </ShootNameBodyText>
                        <EditShootNameButton
                            aria-label={`edit photo shoot name "${hasShootName}"`}
                            onClick={handleOpenNameModal}
                        >
                            <EditIcon size="sm" />
                        </EditShootNameButton>

                        <PhotoshootNameChangeModal
                            defaultShootName={hasShootName}
                            open={isNameModalOpen}
                            onClose={handleCloseNameModal}
                            updateShootName={updateShootName}
                        />
                    </>
                )}
            </NameFlexbox>

            {hasAcceptedFiles.length > 0 && (
                <UploadedFilesControlsFlexbox
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <SelectedPhotosControlsFlexbox alignItems="center">
                        {/* TODO in future PR: clicking select all should select all images checkboxes: */}
                        <CheckboxButton
                            size="xs"
                            label="Select All"
                            inputValue="select-all"
                            ariaId="select-all-photos"
                            initialValue={false}
                            onClick={handleSelectAll}
                        />

                        {/* TODO in future PR: change isSelectAll to instead a const bool that tells us when 1 or more photo is selected: */}
                        {isSelectAll && (
                            <>
                                {/* TODO in future PR: replace [N] with how many photos are selected &
                                        when clicked, open edit photos modal carousel
                                    */}
                                <SelectedPhotosButton
                                    size="xs"
                                    styleType="outline"
                                    onClick={e => handleEditSelected(e)}
                                >
                                    Edit [N] Photos
                                </SelectedPhotosButton>

                                {/* TODO in future PR: clicking delete should delete whatever photos are selected */}
                                <SelectedPhotosButton
                                    size="xs"
                                    styleType="outline"
                                    onClick={e => handleDeleteSelected(e)}
                                >
                                    <TrashIcon />
                                    Delete Selected
                                </SelectedPhotosButton>
                            </>
                        )}
                    </SelectedPhotosControlsFlexbox>

                    <SmallBlueTextButton>
                        Add or Manage Releases
                    </SmallBlueTextButton>
                </UploadedFilesControlsFlexbox>
            )}
        </>
    );
};

HeadingSection.propTypes = {
    hasAcceptedFiles: PropTypes.array,
    hasAddedPhotos: PropTypes.bool.isRequired,
    isPhotoshootType: PropTypes.bool.isRequired,
    hasShootName: PropTypes.string.isRequired,
    allowedUploadNum: PropTypes.number.isRequired,
    radioOptions: PropTypes.array.isRequired,
    handleRadioOnClick: PropTypes.func.isRequired,
    selectedRadio: PropTypes.string.isRequired,
    updateShootName: PropTypes.func.isRequired,
    handleSelectAll: PropTypes.func.isRequired,
    isSelectAll: PropTypes.bool.isRequired
};

export default HeadingSection;
